<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

import PostDockPanel, { type PostArgument } from '../PostDockPanel/PostDockPanel.vue'
import { useChatDock } from '../../chat/AppChatPanel/useChatDock'
import { usePostPanel } from '../../../composables/usePostPanel'
import { useRetained } from '../../../composables/useRetained'
import { toBoardPieces } from '../../../lib/calendar/board'
import { useBrandStore } from '../../../stores/brand'
import { useContentStore } from '../../../stores/content'
import { useDesignStore } from '../../../stores/design'
import { usePlanStore } from '../../../stores/plan'
import { usePostPanelStore, type PostAction } from '../../../stores/post-panel'
import { usePostRunStore } from '../../../stores/post-run'
import { isLoaded } from '../../../types/load-state'

const router = useRouter()
const dock = useChatDock()
const panel = usePostPanelStore()
const { openFull } = usePostPanel()
const content = useContentStore()
const brand = useBrandStore()
const plan = usePlanStore()
const postRun = usePostRunStore()
const design = useDesignStore()

useRetained(plan.retainPlan, postRun.retain)

const item = computed(() => panel.post)

const shown = computed(() => item.value !== null && dock.panel.value === 'post')

const beliefs = computed(() => (isLoaded(brand.beliefMap) ? brand.beliefMap.value.beliefs : []))

const piece = computed(() => {
  const current = item.value
  if (!current) return null
  return (
    toBoardPieces({
      items: [current],
      beliefs: beliefs.value,
      topics: isLoaded(plan.topics) ? plan.topics.value : [],
      pages: isLoaded(plan.pages) ? plan.pages.value : [],
      latestRun: postRun.run,
      articles: isLoaded(content.articles) ? content.articles.value : [],
      results: [],
    })[0] ?? null
  )
})

const argument = computed<PostArgument | null>(() => {
  const slug = item.value?.beliefSlug
  const belief = slug ? beliefs.value.find((entry) => entry.slug === slug) : undefined
  if (!belief) return null
  return { phase: belief.phase, claim: belief.claim }
})

const article = computed(() => {
  const id = item.value?.id
  if (!id || item.value?.shape !== 'article' || !isLoaded(content.articles)) return null
  return content.articles.value.find((entry) => entry.postId === id) ?? null
})

const revisions = computed(() =>
  isLoaded(content.revisions) && content.revisionsPostId === item.value?.id
    ? content.revisions.value
    : [],
)

const revisionsLoading = computed(() => item.value !== null && !isLoaded(content.revisions))

const designRun = computed(() => (item.value ? design.latestRunForPost(item.value.id) : null))

const results = computed(() => (item.value ? content.resultsForPost(item.value.id) : undefined))

function isActionPending(action: PostAction): boolean {
  return item.value !== null && panel.isPending(action, item.value.id)
}

watch(
  () => panel.postId,
  (id, _previous, onCleanup) => {
    if (id) onCleanup(design.retainPostRunFor(id))
  },
  { immediate: true },
)

watch(
  () => panel.postId,
  (id) => {
    if (id) {
      content.subscribeToRevisions(id)
      return
    }
    content.unsubscribeFromRevisions()
    if (dock.panel.value === 'post') dock.closeDock()
  },
)

watch(
  () => [dock.open.value, dock.panel.value] as const,
  ([open, shownPanel]) => {
    if (!open || shownPanel !== 'post') panel.close()
  },
)

function close(): void {
  panel.close()
}

const dockPanel = ref<InstanceType<typeof PostDockPanel> | null>(null)

watch(
  () => (shown.value && dockPanel.value ? panel.dateFocusPostId : null),
  async (id) => {
    if (!id || id !== item.value?.id) return
    await nextTick()
    dockPanel.value?.focusDate()
    panel.dateFocused()
  },
  { flush: 'post' },
)

function onOpenDesign(designId: string): void {
  panel.close()
  void router.push({ name: 'design', params: { designId } })
}

function onRestore(revisionId: string): void {
  if (item.value) void panel.restore(item.value.id, revisionId)
}

function onAskRaldo(id: string, message: string): void {
  panel.askRaldo(id, message)
  dock.showChat()
  dock.openDock()
}
</script>

<template>
  <Teleport defer to="#chat-dock-host">
    <PostDockPanel
      v-if="shown"
      ref="dockPanel"
      :item="item"
      :argument="argument"
      :topic-name="piece?.topicName ?? null"
      :question="piece?.question ?? null"
      :article="article"
      :phase="piece?.phase ?? null"
      :writing="piece?.writing ?? false"
      :results="results"
      :revisions="revisions"
      :current-revision-id="item?.currentRevision"
      :revisions-loading="revisionsLoading"
      :design-run="designRun"
      :is-action-pending="isActionPending"
      @close="close"
      @write-post="openFull"
      @edit="openFull"
      @schedule="panel.schedule"
      @reschedule="panel.reschedule"
      @unschedule="panel.unschedule"
      @remove="panel.remove"
      @publish-now="panel.publishNow"
      @ask-raldo="onAskRaldo"
      @duplicate="panel.duplicate"
      @retry-publish="panel.retryPublish"
      @restore="onRestore"
      @open-design="onOpenDesign"
      @design-media="panel.designMedia"
    />
  </Teleport>
</template>
